import * as React from "react";
import { ConnectProto } from "@racoon/api";
import { toTimestamp } from "@racoon/lib";
import { skipToken } from "@reduxjs/toolkit/query";
import { format } from "date-fns";
import { useGetShiftEngineHoursQuery } from "~/shared/api/analytics/analytics.grpc";
import { useGpsStops } from "~/features/gps-stops";
import { useGetTruckTravelPathQuery } from "~/shared/api/truck/truck.grpc";
import { computeTrackStats, travelPathToGpsPoints } from "~/pages/dashboard/gps-tracking/lib/utils";
import { deriveShiftGps, deriveTripsGps, shiftSearchWindow, tripWindowOf } from "./ttnGps";

type Span = { from: Date; to: Date };

const unionOf = (spans: (Span | null | undefined)[]): Span | null => {
  const present = spans.filter((span): span is Span => Boolean(span));
  if (present.length === 0) return null;
  return {
    from: new Date(Math.min(...present.map((span) => span.from.getTime()))),
    to: new Date(Math.max(...present.map((span) => span.to.getTime()))),
  };
};

/**
 * What the truck's GPS stops say about a shift: its garage exit and entry, and
 * where and when each trip started and unloaded. Read off the stored trip and
 * shift times only — never the TTN draft — so the GPS values hold still while
 * the dispatcher edits.
 */
export const useTtnGps = (
  shift: ConnectProto.DriverOnShift | undefined,
  routes: ConnectProto.Route[],
) => {
  const searchWindow = React.useMemo(
    () => (shift ? shiftSearchWindow(shift, routes) : undefined),
    [shift, routes],
  );

  // One request, covering the shift's search span and every trip of it (a trip
  // can start before the shift is marked started, or end after it closes).
  const stopsWindow = React.useMemo(
    () => unionOf([searchWindow, ...routes.map(tripWindowOf)]),
    [searchWindow, routes],
  );
  // Matched stops only: the derivation reads DEPOT / DISPOSAL rows with a location.
  const { stops } = useGpsStops({
    truckId: shift?.truckId,
    ownerOrganizationId: shift?.ownerOrganizationId,
    from: stopsWindow?.from,
    to: stopsWindow?.to,
  });

  const tripsGps = React.useMemo(() => deriveTripsGps(routes, stops), [routes, stops]);
  const shiftGps = React.useMemo(() => deriveShiftGps(searchWindow, stops), [searchWindow, stops]);

  return { stopsWindow, tripsGps, ...shiftGps };
};

/**
 * What the truck's track says about the shift as it is on screen: the mileage
 * of the shift, of each trip and of the way home, and the engine hours default
 * (the ignition / CAN figure, else the track estimate). Each is the one track
 * cut to its own span, so it follows its times as they are edited.
 */
export const useTtnTrack = (
  shift: ConnectProto.DriverOnShift | undefined,
  /** Выезд → Возвращение as shown. */
  shiftSpan: Span | null,
  /** Each trip's start → unload as shown. */
  tripSpans: Map<bigint, Partial<Span>>,
  /** Where the stops were read; the track covers it too. */
  stopsWindow: Span | null,
) => {
  const trackWindow = React.useMemo(
    () => unionOf([stopsWindow, shiftSpan]),
    [stopsWindow, shiftSpan],
  );
  const { currentData: travelPath } = useGetTruckTravelPathQuery(
    shift && trackWindow
      ? {
          id: shift.truckId,
          ownerOrganizationId: shift.ownerOrganizationId,
          fromTo: { from: toTimestamp(trackWindow.from), to: toTimestamp(trackWindow.to) },
        }
      : skipToken,
  );
  const trackPoints = React.useMemo(
    () =>
      shift && travelPath
        ? travelPathToGpsPoints(
            travelPath.geojsonFeature,
            { id: shift.truckId, plateNumber: "" },
            [],
          )
        : undefined,
    [shift, travelPath],
  );
  // Undefined when the span has no track: "no data", not zero.
  const statsBetween = React.useCallback(
    (from: Date, to: Date) => {
      const points = trackPoints?.filter(
        (point) => point.timestamp >= from && point.timestamp <= to,
      );
      return points && points.length > 1 ? computeTrackStats(points) : undefined;
    },
    [trackPoints],
  );

  const trackStats = React.useMemo(
    () => (shiftSpan ? statsBetween(shiftSpan.from, shiftSpan.to) : undefined),
    [shiftSpan, statsBetween],
  );

  const mileageByRoute = React.useMemo(() => {
    const byRoute = new Map<bigint, number>();
    for (const [routeId, { from, to }] of tripSpans) {
      const stats = from && to ? statsBetween(from, to) : undefined;
      if (stats) byRoute.set(routeId, stats.mileageKm);
    }
    return byRoute;
  }, [tripSpans, statsBetween]);

  // The way home: from the last trip's unload to the shift's return.
  const returnMileage = React.useMemo(() => {
    if (!shiftSpan) return undefined;
    const lastEnd = [...tripSpans.values()].reduce<Date | undefined>(
      (latest, { to }) => (to && (!latest || to > latest) ? to : latest),
      undefined,
    );
    return lastEnd && lastEnd < shiftSpan.to
      ? statsBetween(lastEnd, shiftSpan.to)?.mileageKm
      : undefined;
  }, [tripSpans, shiftSpan, statsBetween]);

  // Engine hours: first the backend's ignition / CAN figure for this shift (the
  // analytics endpoint built for the ТТН page), the track estimate only without it.
  const { currentData: shiftEngineHours } = useGetShiftEngineHoursQuery(
    shift && shiftSpan
      ? {
          ownerOrganizationId: shift.ownerOrganizationId,
          truckId: shift.truckId,
          period: {
            from: format(shiftSpan.from, "yyyy-MM-dd"),
            to: format(shiftSpan.to, "yyyy-MM-dd"),
          },
        }
      : skipToken,
  );
  const sensorEngineHours = React.useMemo(() => {
    const row = shiftEngineHours?.rows.find(
      (r) => shift && String(r.driverOnShiftId) === String(shift.id),
    );
    // NONE means no engine telemetry in the window — absent, not zero.
    const measured =
      row?.source === ConnectProto.EngineHoursSource.IGNITION ||
      row?.source === ConnectProto.EngineHoursSource.CAN_COUNTER;
    // A measured zero is kept; a missing figure falls through to the track estimate.
    return measured && row.engineSeconds != null ? Number(row.engineSeconds) / 3600 : undefined;
  }, [shiftEngineHours, shift]);

  return { trackStats, mileageByRoute, returnMileage, sensorEngineHours };
};
